import { create } from 'zustand';
import type { FileUploaderGalleryProps } from './FileUploaderGallery.types';

interface FileUploaderGalleryStore {
  /** Indicates if the gallery is currently open */
  isOpen: boolean;
  /** Current props of the open gallery instance */
  galleryProps: FileUploaderGalleryProps | null;
  /** 
   * Opens the gallery from anywhere in the system 
   * @param props Gallery configuration properties
   */
  openGallery: (props: FileUploaderGalleryProps) => void;
  /** Closes the active gallery instance */
  closeGallery: () => void;
  /** Updates the current props of the open gallery instance */
  updateGalleryProps: (props: Partial<FileUploaderGalleryProps>) => void;
  /** Specialized update for the resources list */
  updateResources: (uuid: string, checked: boolean) => void;
  /** Specialized update for error messages */
  setError: (error: { title: string; message: string } | null) => void;
}

/**
 * Zustand hook to manage the file gallery globally.
 * 
 * Este store proporciona el método solicitado para abrir la galería
 * (en modo modal o página) desde cualquier parte del frontend.
 */
export const useFileUploaderGallery = create<FileUploaderGalleryStore>((set, get) => ({
  isOpen: false,
  galleryProps: null,
  openGallery: (props) => set({ 
    isOpen: true, 
    galleryProps: {
      ...props,
      // Ensure that the onClose from store triggers the internal closeGallery
      onClose: () => {
        props.onClose?.();
        set({ isOpen: false, galleryProps: null });
      }
    } 
  }),
  closeGallery: () => set({ isOpen: false, galleryProps: null }),
  updateGalleryProps: (props) => set((state) => ({
    galleryProps: state.galleryProps ? { ...state.galleryProps, ...props } : null
  })),
  setError: (error) => set((state) => ({
    galleryProps: state.galleryProps ? { ...state.galleryProps, error } : null
  })),
  updateResources: (uuid, checked) => {
    const { galleryProps } = get();
    if (!galleryProps) return;

    const updatedResources = galleryProps.resources.map(r => 
      r.uuid === uuid ? { ...r, checked } : r
    );

    set({
      galleryProps: {
        ...galleryProps,
        resources: updatedResources
      }
    });
  }
}));
